import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { ScrollIntoView } from '@grandvisionhq/common';
import { Heading } from '@grandvisionhq/elements';
import { RequiredFieldsLabel, DatePicker, CheckboxWithLabel } from '@grandvisionhq/forms';
import { useFeatureToggles, useIntl } from '@grandvisionhq/state';
import { useProductDetailContext } from '../../../../../Context/ProductDetailContext.js';
import { InvalidFormAlert } from './InvalidFormAlert.js';
import { OpticianType, Optician } from './Optician/Optician.js';

const createExternalOpticianInfo = (formState)=>{
    const obj = Object.entries(formState).reduce((acc, [key, field])=>swcHelpers.objectSpread({}, acc, {
            [key]: field ? field.value : ''
        })
    , {});
    return JSON.stringify(obj);
};
const isInfoValid = (state, opticianType)=>{
    const { prescriptionExpiryDate , externalOptician , consent  } = state;
    if (!prescriptionExpiryDate || !consent) {
        return false;
    }
    if (opticianType === OpticianType.External && !externalOptician) {
        return false;
    }
    return true;
};
const getMaxExpiryDate = ()=>{
    const maxYears = 5;
    const date = new Date();
    date.setFullYear(date.getFullYear() + maxYears);
    return date;
};
const PrescriptionInfo = ({ onChange , shouldShowError , errorCount  })=>{
    const { isPrescriptionInfoEnabled  } = useFeatureToggles();
    const { opticianForm  } = useProductDetailContext();
    const { getLabel  } = useIntl();
    const [expiryDate, setExpiryDate] = React.useState();
    const [opticianType, setOpticianType] = React.useState(OpticianType.Internal);
    const [opticianInfo, setOpticianInfo] = React.useState();
    const [consent, setConsent] = React.useState(false);
    const handleExpiryDateChange = React.useCallback((date)=>{
        setExpiryDate(date);
    }, []);
    const handleOpticianTypeChange = React.useCallback((type)=>{
        setOpticianType(type);
    }, []);
    const handleOpticianFormChange = React.useCallback((form)=>{
        if (form.formValid) {
            setOpticianInfo(form.formState);
        }
    }, []);
    const handleConsentChange = React.useCallback(({ checked  })=>{
        setConsent(checked);
    }, []);
    const state = React.useMemo(()=>swcHelpers.objectSpread({
            prescriptionExpiryDate: expiryDate && expiryDate.toISOString()
        }, opticianType === OpticianType.External ? {
            externalOptician: opticianInfo && createExternalOpticianInfo(opticianInfo)
        } : null, {
            consent
        })
    , [
        expiryDate,
        opticianType,
        opticianInfo,
        consent
    ]);
    React.useEffect(()=>{
        if (onChange) {
            onChange(swcHelpers.objectSpread({}, state, {
                valid: isInfoValid(state, opticianType)
            }));
        }
    }, [
        onChange,
        state,
        opticianType
    ]);
    if (!isPrescriptionInfoEnabled) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(React.Fragment, null, shouldShowError === 'scrollToError' && /*#__PURE__*/ React.createElement(ScrollIntoView, {
        key: errorCount
    }, /*#__PURE__*/ React.createElement(InvalidFormAlert, null)), shouldShowError === 'showError' && /*#__PURE__*/ React.createElement(InvalidFormAlert, null), /*#__PURE__*/ React.createElement("div", {
        className: "prescription-info"
    }, /*#__PURE__*/ React.createElement(Heading, {
        className: "prescription-info__heading",
        variant: "smallest"
    }, getLabel('products.contactLenses.info.title')), /*#__PURE__*/ React.createElement(RequiredFieldsLabel, null), opticianForm && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
        className: "prescription-info__dates"
    }, /*#__PURE__*/ React.createElement(DatePicker, {
        "data-t": "prescription-info-dates",
        label: getLabel('products.contactLenses.info.expiryDate.label'),
        tooltip: getLabel('products.contactLenses.info.expiryDate.hint'),
        errorMessage: getLabel('products.contactLenses.info.expiryDate.errorMessage'),
        placeholder: getLabel('common.selectDate'),
        initialValue: expiryDate,
        onChange: handleExpiryDateChange,
        maxDate: getMaxExpiryDate()
    })), /*#__PURE__*/ React.createElement(Optician, {
        opticianForm: opticianForm(),
        opticianFormState: opticianInfo,
        selectedOpticianType: opticianType,
        onOpticianTypeChange: handleOpticianTypeChange,
        onOpticianFormChange: handleOpticianFormChange
    })), /*#__PURE__*/ React.createElement("div", {
        className: "prescription-info__consent"
    }, /*#__PURE__*/ React.createElement(CheckboxWithLabel, {
        className: "prescription-info__checkbox",
        "data-t": "prescription-info-consent",
        id: "prescription-info-consent",
        value: "prescription-info-consent",
        checked: consent,
        onChange: handleConsentChange,
        label: getLabel('products.contactLenses.info.consent'),
        errorMessage: getLabel('common.form.field.required'),
        error: shouldShowError && !consent,
        required: true
    }))));
};

export { PrescriptionInfo, createExternalOpticianInfo, getMaxExpiryDate, isInfoValid };
//# sourceMappingURL=PrescriptionInfo.js.map
